/**
 * Odyssey23 API Service
 * Handles all communication with the Flask backend
 */
import axios from 'axios';

// Base URL - uses proxy in development
const API_BASE = '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Health check
 */
export const checkHealth = async () => {
  const response = await axios.get('/health');
  return response.data;
};

/**
 * Get API info
 */
export const getApiInfo = async () => {
  const response = await api.get('/info');
  return response.data;
};

/**
 * Upload and process a file
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback
 */
export const uploadFile = async (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(`${API_BASE}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });

  return response.data;
};

/**
 * Validate a file (without full processing)
 * @param {File} file - The file to validate
 */
export const validateFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(`${API_BASE}/validate`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Detect anomalies in a file
 * @param {File} file - The file to analyze
 */
export const detectAnomalies = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(`${API_BASE}/detect-anomalies`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Get all processing jobs
 * @param {number} limit - Max jobs to return
 * @param {number} offset - Offset for pagination
 */
export const getJobs = async (limit = 50, offset = 0) => {
  const response = await api.get(`/jobs?limit=${limit}&offset=${offset}`);
  return response.data;
};

/**
 * Get a specific job by ID
 * @param {number} jobId - Job ID
 */
export const getJob = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}`);
  return response.data;
};

/**
 * Delete a job
 * @param {number} jobId - Job ID
 */
export const deleteJob = async (jobId) => {
  const response = await api.delete(`/jobs/${jobId}`);
  return response.data;
};

/**
 * Submit feedback for an anomaly
 * @param {number} anomalyId - Anomaly ID
 * @param {boolean} isTruePositive - Whether the anomaly is a true positive
 * @param {string} correctedValue - Corrected value (optional)
 * @param {string} notes - Notes (optional)
 */
export const submitFeedback = async (anomalyId, isTruePositive, correctedValue, notes) => {
  const response = await api.post('/feedback', {
    anomaly_id: anomalyId,
    is_true_positive: isTruePositive,
    corrected_value: correctedValue,
    notes: notes,
  });
  return response.data;
};

/**
 * Get feedback summary
 */
export const getFeedbackSummary = async () => {
  const response = await api.get('/feedback');
  return response.data;
};

/**
 * Get pending anomalies for review
 * @param {number} limit - Max anomalies to return
 */
export const getPendingReviews = async (limit = 50) => {
  const response = await api.get(`/feedback/pending?limit=${limit}`);
  return response.data;
};

/**
 * Get all jobs with pending reviews, grouped by job
 */
export const getPendingReviewsByJob = async () => {
  const response = await api.get('/feedback/pending-by-job');
  return response.data;
};

/**
 * Get all model versions
 */
export const getModels = async () => {
  const response = await api.get('/models');
  return response.data;
};

/**
 * Get active model
 */
export const getActiveModel = async () => {
  const response = await api.get('/models/active');
  return response.data;
};

/**
 * Activate a model version
 * @param {number} modelId - Model ID
 */
export const activateModel = async (modelId) => {
  const response = await api.post(`/models/${modelId}/activate`);
  return response.data;
};

/**
 * Trigger model retraining
 */
export const retrainModel = async () => {
  const response = await api.post('/models/retrain');
  return response.data;
};

/**
 * Get system statistics
 */
export const getStats = async () => {
  const response = await api.get('/stats');
  return response.data;
};

/**
 * Get recent statistics
 * @param {number} days - Number of days
 */
export const getRecentStats = async (days = 7) => {
  const response = await api.get(`/stats/recent?days=${days}`);
  return response.data;
};

/**
 * Download a file
 * @param {string} filename - Filename to download
 */
export const downloadFile = (filename) => {
  window.open(`${API_BASE}/download/${filename}`, '_blank');
};

/**
 * Preview original and cleaned files for a job with pagination
 * @param {number} jobId - Job ID
 * @param {number} page - Page number (1-indexed)
 * @param {number} perPage - Rows per page
 */
export const getJobPreview = async (jobId, page = 1, perPage = 50) => {
  const response = await api.get(`/jobs/${jobId}/preview?page=${page}&per_page=${perPage}`);
  return response.data;
};

/**
 * Get suggested fix values for an anomaly
 * @param {number} anomalyId - Anomaly ID
 */
export const getSuggestedFix = async (anomalyId) => {
  const response = await api.get(`/anomalies/${anomalyId}/suggest`);
  return response.data;
};

/**
 * Fix an anomaly (edit value, delete value, or delete row)
 * @param {number} anomalyId - Anomaly ID
 * @param {string} action - 'edit', 'delete_value', or 'delete_row'
 * @param {any} newValue - New value (for edit action)
 */
export const fixAnomaly = async (anomalyId, action, newValue = null) => {
  const response = await api.post(`/anomalies/${anomalyId}/fix`, {
    action,
    new_value: newValue
  });
  return response.data;
};

export default api;